import { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import './SchoolsPage.css';
import { fetchAllSchools, handleDeleteSchool } from '../ApiHandler/schoolFunctions';
import AddSchool from '../School/AddSchool';
import ConfirmationModal from '../../utils/ConfirmationModal';

const API_URL = process.env.REACT_APP_API_URL;

const SchoolsPage = () => {
    const [schools, setSchools] = useState([]);
    const [filteredSchools, setFilteredSchools] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [labFilter, setLabFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [editingSchool, setEditingSchool] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [schoolToDelete, setSchoolToDelete] = useState(null);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [showLabModal, setShowLabModal] = useState(false);
    const [schoolLabs, setSchoolLabs] = useState([]);
    const [editingLab, setEditingLab] = useState(null);

    // Assign Teacher State
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [teachers, setTeachers] = useState([]);
    const [selectedTeacherId, setSelectedTeacherId] = useState('');
    
    // eslint-disable-next-line no-unused-vars
    const [labTypes, setLabTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const itemsPerPage = 9;

    // Fetch schools data
    useEffect(() => {
        loadSchools();
        loadLabTypes();
        loadTeachers();
    }, []);

    const loadTeachers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users/fetchUsers`, {
                headers: { 'Authorization': token }
            });
            const data = await response.json();
            if (data.status === 'success') {
                setTeachers(data.data.filter(u => u.role_id === 4));
            }
        } catch (error) {
            console.error('Error loading teachers:', error);
        }
    };

    const loadSchools = async () => {
        try {
            await fetchAllSchools(setSchools);
        } catch (error) {
            console.error('Error loading schools:', error);
            toast.error('Failed to load schools');
        }
    };

    const loadLabTypes = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/labs/lab-types`, {
                headers: { 'Authorization': token }
            });
            const data = await response.json();
            if (data.status === 'success') {
                setLabTypes(data.labTypes || data.data);
            }
        } catch (error) {
            console.error('Error loading lab types:', error);
        }
    };

    const loadSchoolLabs = async (schoolId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/labs/labs-for-school/${schoolId}`, {
                headers: { 'Authorization': token }
            });
            const data = await response.json();
            if (data.status === 'success') {
                setSchoolLabs(data.labs || data.data);
            }
        } catch (error) {
            console.error('Error loading labs:', error);
            toast.error('Failed to load labs');
        }
    };

    const handleViewLabs = async (school) => {
        setSelectedSchool(school);
        setShowLabModal(true);
        await loadSchoolLabs(school.id);
    };

    const handleAssignTeacherClick = (school) => {
        setSelectedSchool(school);
        setSelectedTeacherId('');
        setShowAssignModal(true);
    };

    const handleAssignTeacherSubmit = async () => {
        if (!selectedTeacherId) return toast.error("Please select a teacher");
        try {
            const token = localStorage.getItem('token');
            const teacher = teachers.find(t => t.id === Number(selectedTeacherId));
            const updatedUser = { 
                ...teacher, 
                school_id: selectedSchool.id,
                district_id: selectedSchool.district_id
            };
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users/updateUserDetails`, {
                method: 'PUT',
                headers: { 'Authorization': token, 'Content-Type': 'application/json' },
                body: JSON.stringify({ selectedUser: updatedUser })
            });
            const data = await response.json();
            if (data.status === 'success') {
                toast.success(`School assigned to ${teacher.fname} ${teacher.lname} successfully!`);
                setShowAssignModal(false);
            } else {
                toast.error(data.message || "Failed to assign teacher");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred");
        }
    };

    const handleEditLab = (lab) => {
        setEditingLab({ ...lab });
    };

    const handleSaveLab = async () => {
        if (!editingLab.lab_name.trim()) {
            toast.error('Lab name is required');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/labs/update-lab/${editingLab.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                body: JSON.stringify({
                    lab_name: editingLab.lab_name,
                    lab_type_id: editingLab.lab_type,
                    lab_capacity: editingLab.lab_capacity || 0,
                    lab_description: editingLab.lab_description || '',
                    school_id: selectedSchool.id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                toast.success('Lab updated successfully');
                setEditingLab(null);
                await loadSchoolLabs(selectedSchool.id);
            } else {
                toast.error(data.message || 'Failed to update lab');
            }
        } catch (error) {
            console.error('Error updating lab:', error);
            toast.error('Failed to update lab');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingLab(null);
    };

    // Filter logic
    useEffect(() => {
        let filtered = schools;

        if (searchQuery) {
            filtered = filtered.filter(school =>
                school.school_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                school.udise?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (school.district_name || school.district)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                school.state?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (stateFilter) {
            filtered = filtered.filter(school => school.state === stateFilter);
        }

        if (labFilter) {
            filtered = filtered.filter(school => school.lab_names && school.lab_names.includes(labFilter));
        }

        if (statusFilter !== 'all') {
            const statusValue = statusFilter === 'active' ? 1 : 0;
            filtered = filtered.filter(school => school.school_status === statusValue);
        }

        setFilteredSchools(filtered);
        setCurrentPage(1);
    }, [searchQuery, stateFilter, labFilter, statusFilter, schools]);

    // Get unique states from schools
    const uniqueStates = [...new Set(schools.map(s => s.state))].filter(Boolean).sort();

    // Get unique labs from schools
    const uniqueLabs = [...new Set(schools.flatMap(s => s.lab_names ? s.lab_names.split(',') : []))].filter(Boolean).sort();

    // Pagination
    const totalPages = Math.ceil(filteredSchools.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentSchools = filteredSchools.slice(startIndex, startIndex + itemsPerPage);

    const handleReset = () => {
        setSearchQuery('');
        setStateFilter('');
        setLabFilter('');
        setStatusFilter('all');
    };

    return (
        <div className="schools-page">
            <ToastContainer />
            {/* Header */}
            <div className="schools-page__header">
                <div className="schools-page__header-content">
                    <h1 className="schools-page__title">
                        <i className='bx bxs-school'></i>
                        Schools & Labs Management
                    </h1>
                    <div className="schools-page__stats">
                        <div className="schools-page__stat">
                            <div className="schools-page__stat-label">Total Schools</div>
                            <div className="schools-page__stat-value">{schools.length}</div>
                        </div>
                        <div className="schools-page__stat">
                            <div className="schools-page__stat-label">Active</div>
                            <div className="schools-page__stat-value">
                                {schools.filter(s => s.school_status === 1).length}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="schools-page__filters">
                <div className="schools-page__filters-row">
                    <div className="schools-page__filter-group">
                        <label className="schools-page__filter-label">Search</label>
                        <input
                            type="text"
                            className="schools-page__filter-input"
                            placeholder="Search by name or code..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="schools-page__filter-group">
                        <label className="schools-page__filter-label">State</label>
                        <select
                            className="schools-page__filter-select"
                            value={stateFilter}
                            onChange={(e) => setStateFilter(e.target.value)}
                        >
                            <option value="">All States</option>
                            {uniqueStates.map(state => (
                                <option key={state} value={state}>{state}</option>
                            ))}
                        </select>
                    </div>
                    <div className="schools-page__filter-group">
                        <label className="schools-page__filter-label">Lab</label>
                        <select
                            className="schools-page__filter-select"
                            value={labFilter}
                            onChange={(e) => setLabFilter(e.target.value)}
                        >
                            <option value="">All Labs</option>
                            {uniqueLabs.map(lab => (
                                <option key={lab} value={lab}>{lab}</option>
                            ))}
                        </select>
                    </div>
                    <div className="schools-page__filter-group">
                        <label className="schools-page__filter-label">Status</label>
                        <select
                            className="schools-page__filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                    <div className="schools-page__filter-actions">
                        <button className="schools-page__btn schools-page__btn--secondary" onClick={handleReset}>
                            <i className='bx bx-reset'></i>
                            Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* Schools Grid */}
            {currentSchools.length > 0 ? (
                <>
                    <div className="schools-page__grid">
                        {currentSchools.map((school) => (
                            <div key={school.id} className="schools-page__card">
                                <div className="schools-page__card-header">
                                    <h3 className="schools-page__card-title">{school.school_name}</h3>
                                    <p className="schools-page__card-subtitle">UDISE: {school.udise}</p>
                                </div>
                                <div className="schools-page__card-body">
                                    <div className="schools-page__card-info">
                                        <div className="schools-page__card-info-item">
                                            <i className='bx bx-map schools-page__card-info-icon'></i>
                                            <div className="schools-page__card-info-content">
                                                <div className="schools-page__card-info-label">Location</div>
                                                <div className="schools-page__card-info-value">
                                                    {school.district_name || school.district}, {school.state}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="schools-page__card-info-item">
                                            <i className='bx bx-user schools-page__card-info-icon'></i>
                                            <div className="schools-page__card-info-content">
                                                <div className="schools-page__card-info-label">Contact Person</div>
                                                <div className="schools-page__card-info-value">{school.primary_contact_person || 'N/A'}</div>
                                            </div>
                                        </div>
                                        <div className="schools-page__card-info-item">
                                            <i className='bx bx-phone schools-page__card-info-icon'></i>
                                            <div className="schools-page__card-info-content">
                                                <div className="schools-page__card-info-label">Contact</div>
                                                <div className="schools-page__card-info-value">{school.contact_no || 'N/A'}</div>
                                            </div>
                                        </div>
                                        <div className="schools-page__card-info-item">
                                            <i className='bx bx-envelope schools-page__card-info-icon'></i>
                                            <div className="schools-page__card-info-content">
                                                <div className="schools-page__card-info-label">Email</div>
                                                <div className="schools-page__card-info-value">{school.school_email_id || 'N/A'}</div>
                                            </div>
                                        </div>
                                        <div className="schools-page__card-info-item">
                                            <i className='bx bx-group schools-page__card-info-icon'></i>
                                            <div className="schools-page__card-info-content">
                                                <div className="schools-page__card-info-label">Students</div>
                                                <div className="schools-page__card-info-value">{school.total_students || 'N/A'} Students</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="schools-page__card-footer">
                                    <span className={`schools-page__card-badge schools-page__card-badge--${school.school_status === 1 ? 'active' : 'inactive'}`}>
                                        {school.school_status === 1 ? '✓ Active' : '✕ Inactive'}
                                    </span>
                                    <div className="schools-page__card-actions">
                                        <button
                                            className="schools-page__card-action-btn"
                                            title="Assign Teacher"
                                            onClick={() => handleAssignTeacherClick(school)}
                                        >
                                            <i className='bx bx-user-plus'></i>
                                        </button>
                                        <button
                                            className="schools-page__card-action-btn"
                                            title="Manage Labs"
                                            onClick={() => handleViewLabs(school)}
                                        >
                                            <i className='bx bx-test-tube'></i>
                                        </button>
                                        <button
                                            className="schools-page__card-action-btn"
                                            title={school.school_status === 1 ? 'Force Inactive' : 'Force Active'}
                                            onClick={async () => {
                                                const { handleDisableSchool } = await import('../ApiHandler/schoolFunctions');
                                                await handleDisableSchool(school.id, school.school_name, school.school_status, () => loadSchools());
                                            }}
                                        >
                                            <i className={`bx ${school.school_status === 1 ? 'bx-power-off' : 'bx-check-circle'}`}></i>
                                        </button>
                                        <button
                                            className="schools-page__card-action-btn"
                                            title="Edit School"
                                            onClick={() => setEditingSchool(school)}
                                        >
                                            <i className='bx bx-edit'></i>
                                        </button>
                                        <button
                                            className="schools-page__card-action-btn"
                                            title="Delete School"
                                            onClick={() => { setSchoolToDelete(school); setShowDeleteModal(true); }}
                                        >
                                            <i className='bx bx-trash'></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="schools-page__pagination">
                            <button
                                className="schools-page__pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                            >
                                <i className='bx bx-chevron-left'></i> Previous
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    className={`schools-page__pagination-btn ${currentPage === page ? 'schools-page__pagination-btn--active' : ''}`}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                className="schools-page__pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next <i className='bx bx-chevron-right'></i>
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className="schools-page__empty">
                    <i className='bx bx-search-alt schools-page__empty-icon'></i>
                    <h3 className="schools-page__empty-title">No Schools Found</h3>
                    <p className="schools-page__empty-text">Try adjusting your filters or search query</p>
                </div>
            )}

            {/* Lab Management Modal */}
            {showLabModal && selectedSchool && (
                <div className="modal-overlay" onClick={() => setShowLabModal(false)}>
                    <div className="lab-modal" onClick={e => e.stopPropagation()}>
                        <div className="lab-modal__header">
                            <h2>
                                <i className='bx bx-test-tube'></i>
                                Labs for {selectedSchool.school_name}
                            </h2>
                            <button className="close-btn" onClick={() => setShowLabModal(false)}>
                                <i className='bx bx-x'></i>
                            </button>
                        </div>
                        <div className="lab-modal__content">
                            {schoolLabs.length === 0 ? (
                                <div className="lab-modal__empty">
                                    <i className='bx bx-info-circle'></i>
                                    <p>No labs found for this school</p>
                                </div>
                            ) : (
                                <div className="lab-modal__list">
                                    {schoolLabs.map((lab) => (
                                        <div key={lab.id} className="lab-item">
                                            {editingLab && editingLab.id === lab.id ? (
                                                <div className="lab-item__edit">
                                                    <div className="lab-item__edit-field">
                                                        <label>Lab Name</label>
                                                        <input
                                                            type="text"
                                                            value={editingLab.lab_name}
                                                            onChange={(e) => setEditingLab({ ...editingLab, lab_name: e.target.value })}
                                                            placeholder="Enter lab name"
                                                        />
                                                    </div>
                                                    <div className="lab-item__edit-actions">
                                                        <button
                                                            className="btn-save"
                                                            onClick={handleSaveLab}
                                                            disabled={loading}
                                                        >
                                                            <i className='bx bx-check'></i>
                                                            {loading ? 'Saving...' : 'Save'}
                                                        </button>
                                                        <button
                                                            className="btn-cancel"
                                                            onClick={handleCancelEdit}
                                                            disabled={loading}
                                                        >
                                                            <i className='bx bx-x'></i>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="lab-item__view">
                                                    <div className="lab-item__info">
                                                        <h4>{lab.lab_name}</h4>
                                                        <span className="lab-item__id">Lab ID: {lab.id}</span>
                                                    </div>
                                                    <button
                                                        className="lab-item__edit-btn"
                                                        onClick={() => handleEditLab(lab)}
                                                        title="Edit Lab Name"
                                                    >
                                                        <i className='bx bx-edit'></i>
                                                        Edit
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Edit School Modal */}
            {editingSchool && (
                <div className="modal-overlay" style={{ zIndex: 9999 }}>
                    <div className="modal-content" style={{ maxWidth: '800px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
                        <AddSchool 
                            role={1} 
                            editFormData={editingSchool} 
                            handleClose={() => {
                                setEditingSchool(null);
                                loadSchools();
                            }} 
                        />
                        <button 
                            className="close-btn" 
                            style={{ position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
                            onClick={() => { setEditingSchool(null); loadSchools(); }}
                        >
                            <i className='bx bx-x'></i>
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                title="Delete School"
                message={`Are you sure you want to delete ${schoolToDelete?.school_name}? This action cannot be undone.`}
                onConfirm={async () => {
                    await handleDeleteSchool(schoolToDelete.id, schools, setSchools, () => setShowDeleteModal(false));
                    loadSchools();
                }}
                onCancel={() => { setShowDeleteModal(false); setSchoolToDelete(null); }}
            />
            {/* Assign Teacher Modal */}
            {showAssignModal && selectedSchool && (
                <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
                    <div className="lab-modal" onClick={e => e.stopPropagation()}>
                        <div className="lab-modal__header">
                            <h2>
                                <i className='bx bx-user-plus'></i>
                                Assign Teacher to {selectedSchool.school_name}
                            </h2>
                            <button className="close-btn" onClick={() => setShowAssignModal(false)}>
                                <i className='bx bx-x'></i>
                            </button>
                        </div>
                        <div className="lab-modal__content">
                            <div className="form-group" style={{ marginBottom: '150px' }}>
                                <label style={{ display: 'block', marginBottom: '8px' }}>Select Teacher</label>
                                <Select
                                    options={teachers.map(t => ({ value: t.id, label: `${t.fname} ${t.lname} (${t.email}) - ${t.district_name || 'No District'}` }))}
                                    placeholder="Search & Select a Teacher"
                                    value={teachers.map(t => ({ value: t.id, label: `${t.fname} ${t.lname} (${t.email}) - ${t.district_name || 'No District'}` })).find(o => String(o.value) === String(selectedTeacherId)) || null}
                                    onChange={(selected) => setSelectedTeacherId(selected ? selected.value : '')}
                                    isClearable
                                />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button className="schools-page__btn schools-page__btn--secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
                                <button className="schools-page__btn schools-page__btn--primary" onClick={handleAssignTeacherSubmit}>Assign Teacher</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchoolsPage;
